import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { useFieldProps } from "@/components/ui/form-field"
import { cn } from "@/lib/utils"

/*
   Autosizing by default. Reviewers write reasoning and remediation text
   that runs anywhere from one line to a paragraph, and a fixed-height box
   forces them to either scroll inside a 64px window or drag a resize
   handle. It grows to `maxRows` and only then scrolls.

   `draft` is the domain-specific bit. When the pipeline pre-fills a
   reasoning or remediation field, the reviewer must be able to see at a
   glance that these are not their words yet. A draft renders on the
   printed-amber tint with a marker in the label row, and clears to a
   normal field the moment they type.
*/

const textareaVariants = cva(
  [
    "w-full rounded-md border bg-surface px-3 py-2",
    "text-body text-text placeholder:text-text-disabled",
    "transition-colors duration-120 ease-out",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    "focus-visible:outline-[color:var(--brand)]",
    "disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-disabled",
  ],
  {
    variants: {
      invalid: {
        true: "border-err bg-err-tint/40",
        false: "border-border hover:border-border-strong",
      },
      draft: {
        true: "border-print/40 bg-print-tint/40",
        false: "",
      },
      resizable: {
        true: "resize-y",
        false: "resize-none",
      },
    },
    defaultVariants: { invalid: false, draft: false, resizable: false },
  },
)

export interface TextareaProps
  extends Omit<React.ComponentPropsWithoutRef<"textarea">, "rows">,
    Omit<VariantProps<typeof textareaVariants>, "invalid"> {
  /** Lines shown before it starts growing. Default 3. */
  minRows?: number
  /** Lines it grows to before scrolling. Default 12. */
  maxRows?: number
  /** Turn off autosizing and use a fixed height with a resize handle. */
  autosize?: boolean
  /**
   * Marks the content as model-drafted and not yet the reviewer's words.
   * Renders on the printed-amber tint. Pair with a `draft` marker in the
   * FormField `labelAction`, and clear it on first edit — see the example.
   */
  draft?: boolean
  /**
   * Show a live character count. Requires `maxLength`. Goes amber at 90%
   * and red past the limit, so someone pasting a long remediation note
   * finds out before they lose the tail of it.
   */
  showCount?: boolean
  invalid?: boolean
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    className,
    minRows = 3,
    maxRows = 12,
    autosize = true,
    draft = false,
    resizable = false,
    showCount = false,
    invalid,
    maxLength,
    value,
    onChange,
    ...rest
  },
  forwardedRef,
) {
  const innerRef = React.useRef<HTMLTextAreaElement>(null)
  const ref = useMergedRef(forwardedRef, innerRef)

  const props = useFieldProps({ value, maxLength, ...rest } as typeof rest & {
    value?: React.ComponentPropsWithoutRef<"textarea">["value"]
    maxLength?: number
  })

  const isInvalid =
    invalid ?? (props["aria-invalid"] === true || props["aria-invalid"] === "true")

  /* Autosize: reset to auto so scrollHeight reflects content rather than
     the previous height, then clamp between min and max rows. Runs in a
     layout effect so the frame never paints at the wrong height. */
  React.useLayoutEffect(() => {
    const el = innerRef.current
    if (!el || !autosize) return

    const styles = window.getComputedStyle(el)
    const lineHeight = parseFloat(styles.lineHeight) || 21
    const vPad = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
    const vBorder = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth)

    el.style.height = "auto"
    const min = lineHeight * minRows + vPad + vBorder
    const max = lineHeight * maxRows + vPad + vBorder
    const next = Math.min(Math.max(el.scrollHeight + vBorder, min), max)

    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight + vBorder > max ? "auto" : "hidden"
  }, [value, autosize, minRows, maxRows])

  const length = typeof value === "string" ? value.length : 0
  const ratio = maxLength ? length / maxLength : 0
  const countTone = ratio > 1 ? "text-err" : ratio >= 0.9 ? "text-print" : "text-text-3"

  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        ref={ref}
        rows={autosize ? minRows : undefined}
        className={cn(
          textareaVariants({
            invalid: Boolean(isInvalid),
            draft,
            resizable: !autosize && resizable,
          }),
          autosize && "overflow-hidden",
          className,
        )}
        value={value}
        onChange={onChange}
        {...props}
      />
      {showCount && maxLength ? (
        <div
          className={cn("self-end font-id text-micro tabular-nums", countTone)}
          aria-live="polite"
        >
          {length}/{maxLength}
        </div>
      ) : null}
    </div>
  )
})

/*
   DraftMarker — pairs with `draft` on a Textarea.
   Sits in the FormField's `labelAction` slot. States plainly that the
   content came from the pipeline and that the reviewer owns it once saved,
   which is the whole point: nothing is recorded as a human's words without
   them having seen it.
*/

export function DraftMarker({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-xs bg-print-tint px-1.5 py-0.5",
        "font-id text-micro uppercase tracking-[0.12em] text-print",
        className,
      )}
    >
      Draft — edit before saving
    </span>
  )
}

function useMergedRef<T>(...refs: Array<React.Ref<T> | undefined>): React.RefCallback<T> {
  return React.useCallback(
    (node: T) => {
      for (const ref of refs) {
        if (!ref) continue
        if (typeof ref === "function") ref(node)
        else (ref as React.MutableRefObject<T | null>).current = node
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs,
  )
}
